import { ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { X } from 'lucide-react';

interface Props {
  title: string;
  onClose: () => void | Promise<void>;
  children: ReactNode;
  headerRight?: ReactNode;
  /** Pinned actions at the bottom of the sheet (always visible). */
  footer?: ReactNode;
}

const safeBottom = 'max(env(safe-area-inset-bottom, 0px), 16px)';

export function Sheet({ title, onClose, children, headerRight, footer }: Props) {
  return createPortal(
    <div className="fixed inset-0 z-50 flex items-end justify-center">
      <button
        type="button"
        aria-label="Close"
        className="bt-flat absolute inset-0"
        style={{ background: 'rgba(15,23,42,0.5)', backdropFilter: 'blur(6px)', WebkitBackdropFilter: 'blur(6px)' }}
        onClick={() => void onClose()}
      />
      <div
        className="sheet-panel relative z-10 mx-auto flex w-full max-w-phone max-h-[92dvh] flex-col overflow-hidden rounded-t-3xl shadow-[0_-12px_48px_rgba(15,23,42,0.15),0_-4px_16px_rgba(15,23,42,0.08)]"
        style={{ background: 'linear-gradient(180deg, #ffffff 0%, rgba(250,250,246,0.5) 100%)' }}
      >
        <div className="flex shrink-0 items-center gap-3 border-b border-line/50 bg-paper px-5 py-[18px] pb-4">
          <button type="button" onClick={() => void onClose()} className="flex h-9 w-9 items-center justify-center rounded-[10px] bg-cream text-ink-soft">
            <X size={18} />
          </button>
          <h2 className="flex-1 font-serif text-xl font-bold tracking-[-0.025em] text-ink">{title}</h2>
          {headerRight}
        </div>
        <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain">
          <div className={footer ? 'flex min-h-full flex-col' : undefined}>
            <div className={footer ? 'flex-1' : undefined}>{children}</div>
            {footer && (
              <div
                className="sticky bottom-0 z-10 mt-4 shrink-0 space-y-2 border-t border-line bg-cream px-4 pt-3 shadow-[0_-4px_12px_rgba(10,30,63,0.06)]"
                style={{ paddingBottom: safeBottom }}
              >
                {footer}
              </div>
            )}
          </div>
          {!footer && <div style={{ height: safeBottom, minHeight: 16 }} aria-hidden />}
        </div>
      </div>
    </div>,
    document.body,
  );
}
